import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  createEntityAdapter,
  EntityState,
} from '@reduxjs/toolkit';
import { AppState } from '.';
import {
  AnyPromotionInfo,
  SerializedException,
  exceptionOf,
  findPromotionByCampaignCode,
  findPromotionForProduct,
} from '../models';
import * as PromotionService from '../services/promotion';

export const PROMOTION_FEATURE_KEY = 'promotion';

const promotionAdapter = createEntityAdapter<AnyPromotionInfo>();

interface PromotionState extends EntityState<AnyPromotionInfo> {
  activePromotion: AnyPromotionInfo | null;
  // Every campaign the account is eligible for (GET /v2/promotion/active-campaigns). `undefined`
  // means "not fetched yet" and is deliberately different from `[]` ("fetched, none active"):
  // a card must not advertise a promo price it cannot prove, nor flash the listed price while
  // the campaigns are still in flight.
  activeCampaigns?: AnyPromotionInfo[];
  loading: boolean;
  errors: SerializedException[];
}

const createInitialState = (): PromotionState => promotionAdapter.getInitialState({
  activePromotion: null,
  activeCampaigns: undefined as AnyPromotionInfo[] | undefined,
  loading: false,
  errors: [],
});

// Async thunks
export const doFetchActivePromotion = createAsyncThunk(
  'promotion/fetchActivePromotion',
  async (_, { rejectWithValue }) => {
    try {
      const promotion = await PromotionService.getActivePromotion();
      return promotion;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

export const doFetchActiveCampaigns = createAsyncThunk(
  'promotion/fetchActiveCampaigns',
  async (_, { rejectWithValue }) => {
    try {
      return await PromotionService.getActiveCampaigns();
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const promotionSlice = createSlice({
  name: PROMOTION_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder
      // Fetch active promotion
      .addCase(doFetchActivePromotion.pending, state => {
        state.loading = true;
        state.errors = [];
      })
      .addCase(
        doFetchActivePromotion.fulfilled,
        (state, action: PayloadAction<AnyPromotionInfo | null>) => {
          state.loading = false;
          state.activePromotion = action.payload;
        },
      )
      .addCase(doFetchActivePromotion.rejected, (state, action) => {
        state.loading = false;
        state.errors = [action.payload as SerializedException];
      })
      // Fetch every active campaign (multi-campaign pricing, B.8)
      .addCase(
        doFetchActiveCampaigns.fulfilled,
        (state, action: PayloadAction<AnyPromotionInfo[]>) => {
          state.activeCampaigns = action.payload ?? [];
        },
      )
      .addCase(doFetchActiveCampaigns.rejected, state => {
        // An unreachable endpoint must not pin the cards on a stale promo price: treat it as
        // "no campaign" so every row rests on its listed price.
        state.activeCampaigns = [];
      });
  },
});

// Selectors
const selectPromotionState = (state: AppState) => state[PROMOTION_FEATURE_KEY];

export const selectActivePromotion = (state: AppState) => (
  selectPromotionState(state).activePromotion
);

export const selectActiveCampaigns = (state: AppState) => (
  selectPromotionState(state).activeCampaigns
);

/** The campaign that prices a product, across every campaign the account is eligible for. */
export const selectPromotionForProduct = (productCode: string) => (state: AppState) => (
  findPromotionForProduct(
    selectActiveCampaigns(state),
    selectActivePromotion(state),
    productCode,
  )
);

/** The campaign a preview's applied promotion belongs to (joined by campaign code). */
export const selectPromotionByCampaignCode = (
  campaignCode: string | undefined,
) => (state: AppState): AnyPromotionInfo | null => findPromotionByCampaignCode(
  selectActiveCampaigns(state),
  selectActivePromotion(state),
  campaignCode,
);

// Reducer
export default promotionSlice.reducer;
